import { useContext, ReactNode } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { AuthContext } from "../context/AuthContext";
import { PageLoader } from "./PageLoader/PageLoader";

interface ProtectedRouteProps {
  children?: ReactNode;
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const authContext = useContext(AuthContext);
  const { inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  if (!authContext) {
    throw new Error("AuthContext must be used within an AuthProvider");
  }

  const { loading } = authContext;

  if (inProgress !== InteractionStatus.None || loading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
};

export const ProtectedUserRoute = () => {
  const authContext = useContext(AuthContext);
  const { inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  if (!authContext) {
    throw new Error("AuthContext must be used within an AuthProvider");
  }

  const isOrg = authContext.currentUser?.userType === "organisation";

  if (inProgress !== InteractionStatus.None) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" />;
  }

  return !isOrg ? <Outlet /> : <Navigate to="/org/123" />;
};

export const ProtectedOrgRoute = () => {
  const authContext = useContext(AuthContext);
  const { inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  if (!authContext) {
    throw new Error("AuthContext must be used within an AuthProvider");
  }

  const isOrg = authContext.currentUser?.userType === "organisation";

  if (inProgress !== InteractionStatus.None) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" />;
  }
  return isOrg ? <Outlet /> : <Navigate to="/user/111" />;
};

export const CatchAllRoute = () => {
  const { inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const authContext = useContext(AuthContext);

  if (inProgress !== InteractionStatus.None) {
    return <PageLoader />;
  }

  if (isAuthenticated) {
    const userType = authContext?.currentUser?.userType;
    if (userType === "organisation")
      return <Navigate to="/organisation/events" replace />;
    if (userType === "volunteer")
      return <Navigate to="/volunteer/events" replace />;
    return <PageLoader />;
  }

  return <Navigate to="/login" replace />;
};
